import { useEffect, useRef } from 'react';
import { useMatch } from 'react-router-dom';
import { styled } from '@linaria/react';

import { topLevelNonIframe } from ':fat/helpers/iframe';
import { AppRoutes } from ':fat/routing/routes';

// This component is always rendered on the main page, even when it is on routes
// that don't involve the security frame. Instead of conditionally rendering it,
// this component conditionally hides and shows it. This keeps the iframe
// mounted, which achieves two things:
//
//   - It allows the iframe to hold on to the user's seed phrase in memory,
//     which means it doesn't need to be stored anywhere
//   - It allows the iframe to respond to messages such as signing requests
//
// In addition to keeping the iframe mounted at all times, this component is
// also responsible for ensuring it is actually running on the top-level page
// and not inside an iframe, and breaking out of the iframe if that's not the case
//
export function SecurityFrameWrapper() {
  useEnsureOnMainPage();

  const frameRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    // Refs are set before effects run, so this will always be defined
    MessageBus.registerSecurityFrame(frameRef.current as HTMLIFrameElement);
  }, []);

  const url = window.location.origin + AppRoutes.SECURITY_FRAME.BASE;
  const onLoaderPage = !!useMatch(AppRoutes.SECURITY_FRAME_LOADER);

  return (
    <Frame
      ref={frameRef}
      src={url}
      // @ts-expect-error React and TypeScript disagree about capitalization
      allowtransparency
      display={onLoaderPage ? 'block' : 'none'}
    />
  );
}

function useEnsureOnMainPage() {
  useEffect(() => {
    if (!topLevelNonIframe() && window.top) {
      // eslint-disable-next-line no-console
      console.error('Main page loaded improperly. Redirecting top window to start page.');
      window.top.location.href = window.origin + AppRoutes.SECURITY_FRAME_LOADER;
    }
  }, []);
}

const Frame = styled.iframe<{ display: string }>`
  display: ${(props) => props.display};

  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
`;
